import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const H2 = ({ id, children }) => (
  <h2 id={id} className="text-[22px] md:text-[26px] font-extrabold text-stone-900 mt-10 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
    {children}
  </h2>
);

const H3 = ({ children }) => (
  <h3 className="text-[16px] font-bold text-stone-900 mt-5 mb-2">{children}</h3>
);

const P = ({ children }) => (
  <p className="text-[15px] text-stone-700 leading-relaxed mb-3">{children}</p>
);

const UL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-[15px] text-stone-700 leading-relaxed mb-4">{children}</ul>
);

const PrivacyPolicy = () => {
  const lastUpdated = 'November 2025';
  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <p className="text-[12px] font-bold uppercase tracking-widest text-[#F97316] mb-2">Legal</p>
        <h1 className="text-[38px] md:text-[46px] font-extrabold text-stone-900 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Privacy Policy
        </h1>
        <p className="mt-3 text-[13px] text-stone-500">Last updated: {lastUpdated}</p>

        <div className="mt-10">
          <P>
            This Privacy Policy explains how <strong>Crystal Blue Marketing LLC</strong> ("we," "us," or "our"),
            operating the Ambanji service at ambanji.com, collects, uses, and shares information about you
            when you use our website, applications, and related services (the "Service").
          </P>
          <P>
            By using the Service, you agree to the collection and use of information in accordance with this
            policy. If you do not agree with our practices, please do not use the Service.
          </P>

          <H2 id="info-we-collect">1. Information We Collect</H2>

          <H3>Information you provide</H3>
          <UL>
            <li>Account information: your name, business name, email address, and phone number.</li>
            <li>Business profile details: your Google Business Profile URL, addresses, hours, categories, and keywords you want to rank for.</li>
            <li>Payment information: card details are collected and processed directly by our payment processor, Stripe. We do not store full card numbers on our servers.</li>
            <li>Support communications: any messages you send us by email, SMS, or support form.</li>
          </UL>

          <H3>Information collected automatically</H3>
          <UL>
            <li>Usage data: pages viewed, features used, time on page, and referring URLs.</li>
            <li>Device data: browser type, operating system, IP address, and general location (city-level).</li>
            <li>Essential cookies used to keep you signed in and remember basic preferences.</li>
          </UL>

          <H3>Information collected from third parties</H3>
          <UL>
            <li>Google Business Profile data: when you connect your Google account through the official Google OAuth flow, we receive the specific business data, reviews, insights, posts, and photos your account grants us access to. We never see your Google password.</li>
            <li>Heatmap and ranking data: keyword rank positions and grid coordinates returned by our audit provider based on the business address and keywords you provide.</li>
            <li>Billing and subscription status: from Stripe.</li>
          </UL>

          <H2 id="how-we-use">2. How We Use Your Information</H2>
          <UL>
            <li>Provide, operate, and maintain the Service, including running audits, drafting content, and delivering reports.</li>
            <li>Process subscriptions, invoices, and cancellations through Stripe.</li>
            <li>Send transactional messages: receipts, renewal reminders, security alerts, service updates.</li>
            <li>Draft posts, review replies, and email content on your behalf for your approval.</li>
            <li>Send SMS review request messages on your behalf, only after your customer has consented, using our approved sender number.</li>
            <li>Improve the Service through aggregated, non-identifying analytics.</li>
            <li>Detect and prevent fraud, abuse, or violation of our Terms of Service.</li>
            <li>Comply with legal obligations.</li>
          </UL>

          <H2 id="sms">3. SMS/Text Messaging Policy (A2P-10DLC Compliant)</H2>
          <P>
            Ambanji sends transactional and review-request SMS messages using our A2P-10DLC-registered
            sender number <strong>+1 (614) 902-4952</strong>. The following terms govern all SMS messaging.
          </P>

          <H3>Consent and opt-in</H3>
          <P>
            You will only receive SMS messages from us after providing express written consent by opting in
            through our website, checkout flow, or by explicitly confirming enrollment during account setup.
            Your customers will only receive SMS review requests after your business collects their consent
            through your own opt-in flow. We do not initiate outbound SMS to any phone number without a
            documented opt-in.
          </P>

          <H3>Message frequency</H3>
          <P>
            Message frequency varies based on your subscription tier and activity. Typical frequency is up to
            four (4) messages per month for account-related messaging and additional messages for review
            requests you initiate on behalf of your business.
          </P>

          <H3>Message and data rates</H3>
          <P>
            Message and data rates may apply. Charges are billed by your mobile carrier and are not paid to
            Ambanji.
          </P>

          <H3>Opt-out</H3>
          <P>
            You can opt out of SMS at any time by replying <strong>STOP</strong> to any message from us. You
            will receive one final confirmation message and no further SMS. To rejoin, reply <strong>START</strong>
            or re-enroll from your account settings.
          </P>

          <H3>Help</H3>
          <P>
            Reply <strong>HELP</strong> to any Ambanji SMS to receive contact information for support. You may
            also email us at support@ambanji.com for assistance.
          </P>

          <H3>No sharing of phone numbers</H3>
          <P>
            We do not sell, rent, or share your phone number or your customers' phone numbers with any third
            party for marketing or promotional purposes. Phone numbers are used exclusively to deliver the
            messages you have consented to and to fulfill the Service.
          </P>

          <H2 id="third-parties">4. Third-Party Service Providers</H2>
          <P>
            To operate the Service, we share limited information with the following categories of processors.
            Each provider is contractually or by policy required to protect your information.
          </P>
          <UL>
            <li><strong>Stripe</strong> — payment processing, subscription management, and billing receipts.</li>
            <li><strong>MongoDB Atlas</strong> — encrypted database hosting for account and audit data.</li>
            <li><strong>Local Falcon</strong> — heatmap ranking audits based on your business address and keywords.</li>
            <li><strong>Google (Business Profile API)</strong> — accessing and updating your connected Google Business Profile.</li>
            <li><strong>Our CRM platform</strong> — relaying approved posts, emails, and SMS on your behalf through the Ambanji Relay integration.</li>
            <li><strong>AI content providers</strong> — drafting posts, review replies, and descriptions. Prompts sent to these providers do not include payment data.</li>
            <li><strong>Email delivery</strong> — transactional emails such as receipts and account notifications.</li>
          </UL>

          <H2 id="never-sold">5. Data Sharing and Sale</H2>
          <P>
            <strong>We do not sell your personal information.</strong> We do not share your data with third
            parties for their independent marketing or advertising purposes. We only share data with the
            providers listed above to the extent necessary to run the Service, and with government or law
            enforcement when required by law.
          </P>

          <H2 id="retention">6. Data Retention</H2>
          <P>
            We keep account and business data for as long as your subscription is active and for up to twelve
            (12) months after cancellation to allow for reactivation, dispute resolution, and legal compliance.
            You may request earlier deletion at any time by emailing support@ambanji.com. Billing records may
            be retained longer as required by tax and accounting laws.
          </P>

          <H2 id="your-rights">7. Your Rights</H2>
          <P>Depending on your state or country of residence, you may have the following rights:</P>
          <UL>
            <li><strong>Access</strong> — request a copy of the personal information we hold about you.</li>
            <li><strong>Correction</strong> — ask us to correct information you believe is inaccurate.</li>
            <li><strong>Deletion</strong> — request that we delete your personal information, subject to legal exceptions.</li>
            <li><strong>Portability</strong> — request an export of your data in a machine-readable format.</li>
            <li><strong>Opt-out of sale</strong> — we do not sell personal information, but you may confirm this preference at any time.</li>
            <li><strong>Withdraw consent</strong> — including opting out of SMS and email marketing at any time.</li>
          </UL>
          <P>
            To exercise any of these rights, email support@ambanji.com. We will respond within thirty (30)
            days. We may need to verify your identity before fulfilling certain requests.
          </P>

          <H2 id="cookies">8. Cookies</H2>
          <P>
            We use a small number of essential cookies to keep you signed in, remember basic display
            preferences, and secure the checkout flow. We do not use third-party advertising cookies or
            cross-site trackers. You can block or delete cookies in your browser settings, though this may
            affect functionality.
          </P>

          <H2 id="children">9. Children's Privacy</H2>
          <P>
            The Service is not intended for individuals under thirteen (13) years of age. We do not knowingly
            collect personal information from children. If you believe a child has provided us with personal
            information, please contact us and we will delete it.
          </P>

          <H2 id="international">10. International Users</H2>
          <P>
            Ambanji is operated from the United States. If you access the Service from outside the U.S., you
            understand that your information will be transferred to, stored in, and processed in the United
            States. By using the Service you consent to this transfer.
          </P>

          <H2 id="security">11. Security</H2>
          <P>
            We protect your data with industry-standard measures including HTTPS encryption in transit,
            encryption at rest on our database provider, restricted internal access, and secure OAuth for
            third-party connections. No system is perfectly secure. If we ever learn of a data breach
            affecting you, we will notify you promptly and in accordance with applicable law.
          </P>

          <H2 id="changes">12. Changes to This Policy</H2>
          <P>
            We may update this Privacy Policy from time to time. When we make material changes we will update
            the "Last updated" date above and, when appropriate, provide notice by email or through the
            Service before the changes take effect.
          </P>

          <H2 id="contact">13. Contact Us</H2>
          <P>Questions about this policy or how we handle your data? Reach us at:</P>
          <div className="rounded-xl border border-stone-200 bg-white p-5 text-[14px] text-stone-700 leading-relaxed">
            <p><strong>Crystal Blue Marketing LLC</strong></p>
            <p>501 Westmoor Pl, Columbus, OH 43204</p>
            <p>Email: support@ambanji.com</p>
            <p>SMS sender: +1 (614) 902-4952</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
